import { useState, useRef, useEffect, useLayoutEffect } from "react";
import "./PosCustomFilterV3.css";
import PosCustomIcon from "../PosCustomIcon/PosCustomIcon";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

/**
 * PosCustomerFilterV3 Component
 *
 * A customizable, reusable filter dropdown component with multi-select checkbox options,
 * badge counter, and extensive styling control. Supports both icon and non-icon modes,
 * flexible badge positioning, and circular/square checkbox shapes.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Array<Object|string>} [props.options=[]] - Array of filter options.
 *   - If string: { value: string, label: string }
 *   - If object: Must have `value` and `label` properties
 *   - Example: ['Option 1', 'Option 2'] or [{ value: 'opt1', label: 'Option 1' }]
 *
 * @param {Object} [props.styleProps={}] - Custom styling properties. All properties are optional
 *   and override defaults. Supported properties:
 *   - Button: buttonLabel, buttonLabelFontSize, buttonLabelFontWeight, buttonLabelColor,
 *     buttonColor, buttonRadius, buttonShadow
 *   - Icon: icon (custom JSX), iconColor, iconSize, iconPosition ('left' | 'right')
 *   - Dropdown: dropdownRadius, dropdownBgColor, dropdownMaxHeight (caps visible options and
 *     makes the list scroll internally once exceeded)
 *   - Options: optionsFontSize, optionsFontWeight, optionsTextColor, checkboxOptionGap,
 *     checkboxPosition ('left' | 'right')
 *   - Checkbox: checkboxShape ('circle' | 'square'), checkboxBorderRadius, checkboxBorderColorUnselected,
 *     checkboxBorderColorSelected, checkboxBgUnselected, checkboxBgSelected, checkboxTickColor
 *   - Badge: badgeBg, badgeTextColor, badgeSize, badgePosition
 *     ('top-right' | 'top-left' | 'inline-right' | 'inline-left')
 *
 * @param {Function} [props.onChange] - Callback fired when selection changes.
 *   Receives array of selected values as argument.
 *   Example: onChange={(selected) => console.log(selected)}
 *
 * @returns {React.ReactElement} A dropdown filter component with checkbox options and badge counter
 *
 * @example
 * // Basic usage with string options
 * <PosCustomerFilterV3
 *   options={['Online', 'In-Store', 'Phone']}
 *   onChange={(selected) => setFilters(selected)}
 * />
 *
 * @example
 * // Advanced usage with custom styling
 * <PosCustomerFilterV3
 *   options={[
 *     { value: 'swiggy', label: 'Swiggy' },
 *     { value: 'zomato', label: 'Zomato' },
 *     { value: 'pos', label: 'POS' }
 *   ]}
 *   styleProps={{
 *     buttonLabel: 'Select Sources',
 *     buttonLabelColor: '#2563eb',
 *     badgeBg: '#ef4444',
 *     checkboxShape: 'square',
 *     badgePosition: 'top-right',
 *     iconPosition: 'left'
 *   }}
 *   onChange={(selected) => handleFilterChange(selected)}
 * />
 *
 * @features
 * - Multi-select checkbox functionality
 * - Dynamic badge counter showing selected count
 * - Click-outside auto-close behavior
 * - Fully customizable colors, sizes, and positions
 * - Supports circular or square checkboxes
 * - Flexible badge positioning (absolute or inline)
 * - Custom or default chevron icon
 */

// ── Defaults ──────────────────────────────────────────────────────────────────

const defaultStyles = {
  // Button
  buttonLabel: "Filter by Source",
  buttonLabelFontSize: "14px",
  buttonLabelFontWeight: "500",
  buttonLabelColor: "#1a1a2e",
  buttonColor: "#ffffff",
  buttonRadius: "10px",
  buttonShadow: "0 2px 8px rgba(0,0,0,0.10)",
  buttonBorder: "1px solid #e5e7ef",
  buttonWidth: undefined,
  buttonPadding: "8px 16px",

  // Icon
  icon: null, // null = default chevron SVG
  iconColor: "#1a1a2e",
  iconSize: "16px",
  iconPosition: "right", // "left" | "right"

  // Dropdown
  dropdownRadius: "14px",
  dropdownBgColor: "#ffffff",
  dropdownMaxHeight: undefined,

  // Options
  optionsFontSize: "14px",
  optionsFontWeight: "500",
  optionsTextColor: "#1a1a2e",
  checkboxOptionGap: "10px",
  checkboxPosition: "left", // "left" | "right"

  // Checkbox
  checkboxShape: "circle", // "circle" | "square"
  checkboxBorderRadius: "4px",
  checkboxBorderColorUnselected: "#d0d5e8",
  checkboxBorderColorSelected: "#2563eb",
  checkboxBgUnselected: "#ffffff",
  checkboxBgSelected: "#2563eb",
  checkboxTickColor: "#ffffff",

  // Badge
  badgeBg: "#2563eb",
  badgeTextColor: "#ffffff",
  badgeSize: "18px",
  badgePosition: "top-right", // "top-right" | "top-left" | "inline-right" | "inline-left"
};

// ── Component ─────────────────────────────────────────────────────────────────

const valuesEqual = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((val, index) => val === b[index]);
  }
  return a === b;
};

export default function PosCustomFilterV3({
  options = [],
  styleProps = {},
  onChange,
  selectedValues = [],
}) {
  const s = { ...defaultStyles, ...styleProps };

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(selectedValues || []);
  const [dropdownAlign, setDropdownAlign] = useState("left");

  // Sync selected state with selectedValues prop
  useEffect(() => {
    setSelected(selectedValues || []);
  }, [selectedValues]);

  const dropdownRef = useRef(null);
  const wrapperRef = useRef(null);

  // Resolve checkbox border-radius from shape
  const checkboxBorderRadius = s?.checkboxShape === "circle" ? "50%" : s?.checkboxBorderRadius;

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useLayoutEffect(() => {
    if (!open || !wrapperRef.current || !dropdownRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();

    const viewportWidth = window.innerWidth;

    const spaceRight = viewportWidth - wrapperRect.left;
    const spaceLeft = wrapperRect.right;

    // If dropdown overflows on right side → align right
    if (spaceRight < dropdownRect.width && spaceLeft >= dropdownRect.width) {
      setDropdownAlign("right");
    } else {
      setDropdownAlign("left");
    }
  }, [open]);

  const toggleOption = (val) => {
    const next = selected?.some?.((v) => valuesEqual(v, val))
      ? selected?.filter?.((v) => !valuesEqual(v, val))
      : [...selected, val];
    setSelected(next);
    onChange?.(next);
  };

  const selectedCount = selected?.length;
  const isInlineBadge = s?.badgePosition === "inline-right" || s?.badgePosition === "inline-left";

  // ── Badge ──────────────────────────────────────────────────────────────────
  // CSS class handles position; inline style handles only prop-driven colours/sizes.
  const badge = selectedCount > 0 && (
    <span
      className={`filter-v3-badge filter-v3-badge--${s.badgePosition}`}
      style={{
        minWidth: s?.badgeSize,
        height: s?.badgeSize,
        background: s?.badgeBg,
        color: s?.badgeTextColor,
        fontSize: `calc(${s?.badgeSize} * 0.6)`,
      }}
    >
      {selectedCount}
    </span>
  );

  // ── Icon ───────────────────────────────────────────────────────────────────
  const iconEl = s?.icon ? (
    <span className="fitler-v3-icon">
      <PosCustomIcon icon={s?.icon} color={s?.iconColor} height={s?.iconSize} width={s?.iconSize} />
    </span>
  ) : (
    <span className={`filter-v3-icon${open ? "filter-v3-icon--open" : ""}`}>
      <PosCustomIcon
        icon={<ChevronDownIcon />}
        color={s?.iconColor}
        height={s?.iconSize}
        width={s?.iconSize}
      />
    </span>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="filter-v3-wrapper"
      ref={wrapperRef}
      style={s?.buttonWidth ? { width: s.buttonWidth } : undefined}
    >
      {/* Trigger button */}
      <button
        className={`filter-v3-button filter-v3-button--icon-${s.iconPosition}`}
        onClick={() => setOpen((o) => !o)}
        style={{
          background: s?.buttonColor,
          borderRadius: s?.buttonRadius,
          boxShadow: s?.buttonShadow,
          border: s?.buttonBorder,
          padding: s?.buttonPadding,
          ...(s?.buttonWidth && { width: "100%" }),
        }}
      >
        {isInlineBadge && s?.badgePosition === "inline-left" && badge}

        <span
          className="filter-v3-button-label"
          style={{
            fontSize: s?.buttonLabelFontSize,
            fontWeight: s.buttonLabelFontWeight,
            color: s?.buttonLabelColor,
          }}
        >
          {s?.buttonLabel}
        </span>

        {isInlineBadge && s?.badgePosition === "inline-right" && badge}

        {iconEl}

        {/* Absolute-positioned badge (top-right / top-left) */}
        {!isInlineBadge && badge}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          ref={dropdownRef}
          className={`filter-v3-dropdown filter-v3-dropdown--${dropdownAlign}`}
          style={{
            background: s?.dropdownBgColor,
            borderRadius: s?.dropdownRadius,
            boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
          }}
        >
          <div
            className={`filter-v3-options-list${
              s?.dropdownMaxHeight ? " filter-v3-options-list--scrollable" : ""
            }`}
            style={{
              maxHeight: s?.dropdownMaxHeight,
              overflowY: s?.dropdownMaxHeight ? "auto" : undefined,
            }}
          >
            {options?.map((opt) => {
              const value = opt?.value ?? opt;
              const label = opt?.label ?? opt;
              const isSelected = selected?.some?.((v) => valuesEqual(v, value));

              return (
                <div
                  key={value}
                  className={`filter-v3-option filter-v3-option--checkbox-${s?.checkboxPosition}`}
                  onClick={() => toggleOption(value)}
                  style={{ gap: s?.checkboxOptionGap }}
                >
                  {/* Checkbox — only colour/radius are prop-driven */}
                  <span
                    className="filter-v3-checkbox"
                    style={{
                      borderRadius: checkboxBorderRadius,
                      borderColor: isSelected
                        ? s?.checkboxBorderColorSelected
                        : s?.checkboxBorderColorUnselected,
                      background: isSelected ? s?.checkboxBgSelected : s?.checkboxBgUnselected,
                    }}
                  >
                    {isSelected && (
                      <PosCustomIcon
                        icon={<CheckIcon />}
                        color={s?.checkboxTickColor}
                        height="12px"
                        width="12px"
                      />
                    )}
                  </span>

                  {/* Option label */}
                  <span
                    className="filter-v3-option-text"
                    style={{
                      fontSize: s?.optionsFontSize,
                      fontWeight: s?.optionsFontWeight,
                      color: s?.optionsTextColor,
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
